/**
 * Characteristic reads — compute-on-read from the printed base (SPEC.md §4.5).
 *
 * Nothing is cached. Every read recomputes from the `CardDef` plus the live modifier records
 * and the continuous static abilities of cards in play, which is exactly what CR §6.6's "all
 * modifiers combine at once, recompute on every change" asks for. Callers must go through
 * these accessors, so ticket 10's general continuous-effect model is a change in one place.
 *
 * Three sources are summed on top of the printed value:
 *
 * 1. **Modifier records** (`GameState.modifiers`) — what a resolved `modify` effect leaves
 *    behind: an applied static, scoped to the cards it snapshotted, with a duration and an
 *    optional "while" guard (`modifiers.ts`).
 * 2. **Continuous statics** — a `{ kind: 'static', effect: { kind: 'buff' } }` ability on any
 *    card in play whose filter this card matches (CR §6.4.2.1). Dynamic by construction: a
 *    card that arrives later is affected, because nothing is snapshotted.
 * 3. **Challenger +N** — the one keyword that modifies {S}, and only while this card is the
 *    challenger of a live challenge (CR §8.4).
 *
 * The sum is unclamped, because CR §6.6.2/§6.6.3 clamp at the *reader*, not in the
 * arithmetic: a −2 {S} character stays at −2 for further modification, while everything that
 * *looks at* its {S} — dealing damage, an effect's limiter, "equal to their {S}" — reads 0.
 * `effectiveStrengthOf`/`effectiveLoreOf` are those readers.
 */
import { challengerBonusOf, hasKeyword } from './abilities/keywords.ts';
import type { Stat } from './abilities/ir.ts';
import type { CardDefinition } from './cards.ts';
import { requireDefinition } from './cards.ts';
import type { InstanceId } from './ids.ts';
import {
  challengeDamageStatOf,
  hasPrintedFloor,
  isAllowed,
  isRestricted,
  statBonus,
} from './modifiers.ts';
import type { CardInstance, GameState } from './state.ts';

export function definitionOf(state: GameState, instance: CardInstance): CardDefinition {
  return requireDefinition(state.catalog, instance.defId);
}

/**
 * Every modifier and continuous static that adds to one characteristic of one card, combined
 * simultaneously and recomputed from scratch on every read (CR §6.6, SPEC.md §4.5).
 */
function bonusFor(state: GameState, instance: CardInstance, stat: Stat): number {
  return statBonus(state, instance, stat);
}

/**
 * Whether this card is the challenger of a challenge currently on the resolution stack —
 * the "while challenging" window that Challenger +N applies in (CR §8.4, §4.6). Read off the
 * stack rather than stored, so it needs no cleanup and no extra state field.
 */
export function isChallenging(state: GameState, id: InstanceId): boolean {
  return state.stack.some((frame) => frame.kind === 'challenge' && frame.challenger === id);
}

/**
 * A modified total, clamped up to `base` when a live `StaticEffect.stat-floor` covers this
 * card for `stat` (Elisa Maza - Transformed Gargoyle's FOREVER STRONG) — the one exception to
 * "unclamped" documented above. It only ever raises a *reduced* total back to the printed
 * value: an ordinary bonus that already sits at or above `base` never consults the floor at
 * all, which is what "retains ordinary bonuses above the floor" means in practice.
 */
function withPrintedFloor(
  state: GameState,
  instance: CardInstance,
  stat: Stat,
  base: number,
  raw: number,
): number {
  if (raw >= base) return raw;
  return hasPrintedFloor(state, instance, stat) ? base : raw;
}

/**
 * {S}, as modified — **possibly negative** (CR §6.6.2: a negative {S} stays negative for
 * further modification, so this is what another modifier adds to) unless a printed floor
 * covers this card, in which case it never falls below its printed {S}. Use
 * `effectiveStrengthOf` wherever the game *checks* a character's {S}.
 */
export function strengthOf(state: GameState, instance: CardInstance): number {
  const base = definitionOf(state, instance).strength ?? 0;
  const challenging = isChallenging(state, instance.id) ? challengerBonusOf(state, instance) : 0;
  const raw = base + bonusFor(state, instance, 'strength') + challenging;
  return withPrintedFloor(state, instance, 'strength', base, raw);
}

/** {S} as the rules read it: a negative {S} counts as 0 (CR §6.6.2). */
export function effectiveStrengthOf(state: GameState, instance: CardInstance): number {
  return Math.max(0, strengthOf(state, instance));
}

/** {W}. A card is banished at a game state check once `damage >= willpower` (CR §1.8). */
export function willpowerOf(state: GameState, instance: CardInstance): number {
  const base = definitionOf(state, instance).willpower ?? 0;
  const raw = base + bonusFor(state, instance, 'willpower');
  return withPrintedFloor(state, instance, 'willpower', base, raw);
}

/** Lore value, as modified — possibly negative, for the same reason as {S} (CR §6.6.3). */
export function loreOf(state: GameState, instance: CardInstance): number {
  const base = definitionOf(state, instance).lore ?? 0;
  const raw = base + bonusFor(state, instance, 'lore');
  return withPrintedFloor(state, instance, 'lore', base, raw);
}

/** Lore value as the rules read it: negative yields 0 lore when questing (CR §6.6.3). */
export function effectiveLoreOf(state: GameState, instance: CardInstance): number {
  return Math.max(0, loreOf(state, instance));
}

/**
 * Total cost to play from hand (CONTEXT.md "Total cost") — the printed ink cost plus every
 * payment modifier that applies, floored at zero.
 *
 * The model itself lives in `costs.ts` (ticket 09): Underdog, floating cost records, static
 * cost modifiers and the alternate costs that replace this number outright. It is re-exported
 * here because this is where characteristic reads live and every caller already looks here.
 */
export { costOf } from './costs.ts';

/**
 * Damage this card would deal in a challenge — normally its {S}, or the live player-scoped
 * replacement characteristic when a static names one, clamped at 0 (CR §6.6.2).
 */
export function damageDealtBy(state: GameState, instance: CardInstance): number {
  const type = definitionOf(state, instance).type;
  // Locations have no {S} and deal no damage back (CR §5.6).
  if (type === 'Location') return 0;
  if (challengeDamageStatOf(state, instance.controller) === 'willpower') {
    return Math.max(0, willpowerOf(state, instance));
  }
  return effectiveStrengthOf(state, instance);
}

/**
 * Whether this card can be exerted to pay a cost — in play, dry, and ready (CR §5.1).
 *
 * This is the `{E}` predicate: **Rush does not satisfy it** (CR §8.8 — Rush lets a character
 * challenge, not quest or exert). `canQuest` and `canChallenge` are the action-specific
 * predicates and are what `legalTurnActions` uses.
 */
export function canExertForAction(state: GameState, instance: CardInstance): boolean {
  return (
    instance.zone === 'play' &&
    !instance.exerted &&
    !instance.drying &&
    definitionOf(state, instance).type === 'Character'
  );
}

/**
 * Whether one card can pay an `{E}` cost component: in play and ready, and — for a character
 * only — dry (CR §4.4.2). An item or location need not be dry (CR §5.5), which is what lets
 * "⟳ one of your items —" take an item played this turn. Shared by an activated ability's
 * `exert-cards` cost (`abilities/interpret.ts`) and an `exert` alternate cost (`payments.ts`).
 */
export function canPayExertCost(state: GameState, instance: CardInstance): boolean {
  if (definitionOf(state, instance).type === 'Character') {
    return canExertForAction(state, instance);
  }
  return instance.zone === 'play' && !instance.exerted;
}

/**
 * Quest legality (CR §4.5): a ready character that is dry — or has a specific permission to
 * quest while drying. **Reckless can't quest** (CR §8.7), nor can one a continuous effect has
 * forbidden from questing. Those "can't" effects still win over the drying bypass (CR §6.6.5).
 */
export function canQuest(state: GameState, instance: CardInstance): boolean {
  if (instance.zone !== 'play' || instance.exerted) return false;
  if (definitionOf(state, instance).type !== 'Character') return false;
  if (isRestricted(state, instance, 'quest')) return false;
  if (hasKeyword(state, instance, 'Reckless')) return false;
  return !instance.drying || isAllowed(state, instance, 'quest-while-drying');
}

/**
 * Whether the Ready step readies this card (CR §3.2.1). False only while a continuous effect
 * says otherwise — "that character can't ready at the start of their next turn".
 */
export function canReady(state: GameState, instance: CardInstance): boolean {
  return !isRestricted(state, instance, 'ready');
}

/**
 * Challenge legality for the challenger (CR §4.6.3): a ready character that is dry — or has
 * **Rush**, which lets it challenge as though it had been in play since the turn began
 * (CR §8.8).
 */
export function canChallenge(state: GameState, instance: CardInstance): boolean {
  if (instance.zone !== 'play' || instance.exerted) return false;
  if (definitionOf(state, instance).type !== 'Character') return false;
  if (isRestricted(state, instance, 'challenge')) return false;
  return !instance.drying || hasKeyword(state, instance, 'Rush');
}
